/**
 * Selecting rows of a list to act on them together (the Trash, the items table, the triage area): a checkbox per row, one
 * that selects the page's rows (half checked while some are), and while anything is selected, "N selected · actions
 * · Clear selection" at the end of the list's toolbar row, beside its search and filters (which stay usable: what's
 * selected stays so across pages and searches). Once the whole page is, the count offers every row the list matches
 * in its own place ("50 selected · Select all 5,530", then "All 5,530 selected"), as Gmail does, so nothing moves.
 * The selection itself is `lib/selection.ts`.
 */
import type { ComponentChildren } from "preact";

import { t } from "../i18n/index.ts";
import type { Selection } from "../lib/selection.ts";
import { useId } from "../lib/uid.ts";
import { Button } from "./Button.tsx";
import { LINK } from "./styles.ts";
import { Toolbar, type ToolbarItem } from "./Toolbar.tsx";

const CHECKBOX = "size-4 shrink-0 rounded border-zinc-300 accent-blue-600";

/** A row's checkbox. */
export function SelectBox(p: { sel: Selection; id: string; title: string; class?: string }) {
  return (
    <input
      type="checkbox"
      class={`${CHECKBOX} ${p.class ?? ""}`}
      aria-label={t("selection.select", { title: p.title })}
      checked={p.sel.has(p.id)}
      onChange={(e) => p.sel.toggle(p.id, e.currentTarget.checked)}
    />
  );
}

/** The head's checkbox: all of the page's rows (`ids`), or none (half checked while some are); with `count`, it says how many. */
export function SelectAll(p: { sel: Selection; ids: readonly string[]; count?: boolean; class?: string; id?: string }) {
  const picked = p.ids.filter((id) => p.sel.has(id)).length;
  const all = p.ids.length > 0 && picked === p.ids.length;
  const label = p.count ? t("selection.selectAllCount", { count: p.ids.length }) : t("selection.selectAll");
  return (
    <input
      type="checkbox"
      id={p.id}
      class={`${CHECKBOX} ${p.class ?? ""}`}
      aria-label={label}
      title={p.count ? label : undefined}
      checked={all}
      indeterminate={picked > 0 && !all}
      onChange={(e) => p.sel.setAll(p.ids, e.currentTarget.checked)}
    />
  );
}

/** What a list shows, for its selection: the page's ids and how many rows match in all. */
export interface Shown {
  ids: readonly string[];
  total: number;
  /** The list is searched or filtered ("matching this search"), not the whole of it ("in this list"). */
  filtered: boolean;
}

/**
 * For a list without a toolbar row of its own (the triage area's): one with "Select all N on this page" on the left and,
 * while anything is selected, the selection's bar at its end (or, while a bulk job runs, its progress). Always there,
 * so selecting doesn't move the list.
 */
export function SelectionRow(p: {
  sel: Selection;
  shown: Shown;
  /** What the selection can have done, given its bar to put them in (with what they open beside it). */
  children?: (bar: (items: readonly ToolbarItem[]) => ComponentChildren) => ComponentChildren;
  /** A bulk job's progress, in the bar's place while it runs. */
  job?: ComponentChildren;
}) {
  const id = useId();
  const { ids } = p.shown;
  const bar = (items: readonly ToolbarItem[]) => <SelectionBar sel={p.sel} shown={p.shown} items={items} />;
  return (
    <div class="mb-2 flex min-h-8 items-center gap-3 px-4">
      <SelectAll sel={p.sel} ids={ids} count={true} id={id} />
      <label for={id} aria-hidden="true" class="shrink-0 text-sm text-zinc-600">
        {t("selection.selectAllCount", { count: ids.length })}
      </label>
      {/* (The bar measured against what's left of the row, not all of it: else it counts the room the label takes, and
          what it folds into its "⋯" is cut off, the "⋯" too.) */}
      <div class="flex min-w-0 flex-1 justify-end">
        {p.job ?? (p.sel.ids.length > 0 ? (p.children?.(bar) ?? bar([])) : null)}
      </div>
    </div>
  );
}

/**
 * How many are selected, where the count stands (so nothing moves as it changes): every match ("All 5,589
 * selected"); the whole page of a longer list, with every match offered Gmail-style ("50 selected · Select all
 * 5,589"); else those picked (saying how many of them the page shows, when not all).
 */
function Count({ sel, shown }: { sel: Selection; shown: Shown | undefined }) {
  if (sel.all && shown) {
    return <>{t("selection.allSelected", { count: shown.total })}</>;
  }
  const n = sel.ids.length;
  const here = shown ? shown.ids.filter((id) => sel.has(id)).length : n;
  if (here < n) {
    return <>{t("selection.selectedOffPage", { count: n, page: here })}</>;
  }
  const more = shown && shown.ids.length > 0 && here === shown.ids.length && shown.total > shown.ids.length;
  return (
    <>
      {t("selection.selected", { count: n })}
      {more ? (
        <>
          {" · "}
          <button
            type="button"
            class={LINK}
            title={t(shown.filtered ? "selection.matchAll" : "selection.listAll", { count: shown.total })}
            onClick={() => sel.selectMatching(shown.ids)}
          >
            {t("selection.selectAllN", { count: shown.total })}
          </button>
        </>
      ) : null}
    </>
  );
}

/**
 * "2 selected · <actions> · Clear selection", at the end of the toolbar row it's put in (taking what's left of it).
 * Narrow, the actions (by their own `drop`) and clearing go into its "⋯"; the count stays.
 */
export function SelectionBar(p: { sel: Selection; shown?: Shown | undefined; items?: readonly ToolbarItem[] }) {
  return (
    <Toolbar
      label={t("selection.label")}
      hug={true}
      class="ml-auto text-sm"
      items={[
        {
          key: "count",
          node: (
            <span class="mr-1 text-zinc-600 tabular-nums">
              <Count sel={p.sel} shown={p.shown} />
            </span>
          ),
        },
        ...(p.items ?? []),
        {
          key: "clear",
          node: (
            <Button size="sm" variant="ghost" onClick={p.sel.clear}>
              {t("selection.clear")}
            </Button>
          ),
          drop: 60,
          menu: [{ label: t("selection.clear"), onSelect: p.sel.clear }],
        },
      ]}
    />
  );
}
